/**
 * webVitals.ts
 *
 * Reports Core Web Vitals (LCP, FID/INP, CLS) and our own custom timings
 * to the Box backend telemetry endpoint.
 *
 * Per the latency commitment (p50 < 100ms, p99 < 300ms warm AND cold):
 * - We MUST measure to know if we're holding the SLO
 * - Web Vitals capture user-perceived performance globally
 * - Custom timing (viewer.click → viewer.firstImagePainted) captures
 *   the specific metric we committed to in the CEO plan
 *
 * Lightweight implementation — no third-party dependency. Uses native
 * PerformanceObserver. ~1KB gzipped.
 */

import { getApiBase } from '@services/api/apiConfig';

interface VitalReport {
  name: 'LCP' | 'FID' | 'INP' | 'CLS' | 'FCP' | 'TTFB';
  value: number;
  id: string;
  route: string;
  cacheState?: 'HIT' | 'MISS' | 'STALE' | 'BYPASS' | 'unknown';
}

interface CustomTiming {
  name: string;
  durationMs: number;
  route: string;
  metadata?: Record<string, string | number | boolean>;
}

const TELEMETRY_ENDPOINT = '/api/telemetry/vitals';

function isTelemetryEnabled(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_ENABLE_TELEMETRY === 'true';
}

/**
 * Fire-and-forget POST to telemetry endpoint. Uses sendBeacon when available
 * (survives page unload); fetch with keepalive otherwise.
 */
function sendReport(payload: VitalReport | CustomTiming): void {
  if (!isTelemetryEnabled()) return;
  const url = `${getApiBase()}${TELEMETRY_ENDPOINT}`;
  const body = JSON.stringify({
    ...payload,
    ts: new Date().toISOString(),
    ua: navigator.userAgent.slice(0, 200),
  });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true });
    }
  } catch {
    // Telemetry failure is silent — never break the user's experience to ship a metric
  }
}

/**
 * Initialize Web Vitals observers. Safe to call multiple times (idempotent
 * via the global flag). No-op in non-browser environments.
 */
declare global {
  interface Window { __gboxVitalsInited?: boolean; }
}

export function initWebVitals(): void {
  if (typeof window === 'undefined' || window.__gboxVitalsInited || !isTelemetryEnabled()) return;
  window.__gboxVitalsInited = true;

  const route = () => window.location.pathname;

  // LCP — Largest Contentful Paint
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      if (last) {
        sendReport({ name: 'LCP', value: Math.round(last.startTime), id: crypto.randomUUID(), route: route() });
      }
    }).observe({ type: 'largest-contentful-paint', buffered: true });
  } catch { /* not all browsers */ }

  // CLS — Cumulative Layout Shift
  try {
    let cls = 0;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as Array<PerformanceEntry & { value: number; hadRecentInput?: boolean }>) {
        if (!entry.hadRecentInput) cls += entry.value;
      }
    }).observe({ type: 'layout-shift', buffered: true });
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        sendReport({ name: 'CLS', value: Math.round(cls * 1000) / 1000, id: crypto.randomUUID(), route: route() });
      }
    });
  } catch { /* */ }

  // INP — Interaction to Next Paint (replaces FID in Chrome)
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as Array<PerformanceEntry & { duration: number }>) {
        sendReport({ name: 'INP', value: Math.round(entry.duration), id: crypto.randomUUID(), route: route() });
      }
    }).observe({ type: 'event', durationThreshold: 40, buffered: true } as PerformanceObserverInit);
  } catch { /* */ }

  // FCP + TTFB from navigation timing
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav) {
      sendReport({ name: 'TTFB', value: Math.round(nav.responseStart - nav.requestStart), id: crypto.randomUUID(), route: route() });
    }
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          sendReport({ name: 'FCP', value: Math.round(entry.startTime), id: crypto.randomUUID(), route: route() });
        }
      }
    }).observe({ type: 'paint', buffered: true });
  } catch { /* */ }
}
